import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProducts } from "../../hooks/useProducts";
import { ProductService } from "../../services/productService";
import { Product } from "../../types/product";
import AdminInventoryMobile from "./AdminInventoryMobile"; // New mobile version
import { ConfirmationDialog } from "../admin/ConfirmationDialog";
import AdminInventoryPage from "../admin/AdminInventoryPage copy";

const ResponsiveAdminInventory: React.FC = () => {
  const navigate = useNavigate();
  const { products, isLoading, error,refetch} = useProducts();
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Check screen size for responsive rendering
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    // Initial check
    checkScreenSize();

    // Add event listener for resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const handleDeleteProduct = async (): Promise<void> => {
    if (!deletingProductId) return;
    
    try {
      await ProductService.deleteProductById(deletingProductId);
      refetch();
      setDeletingProductId(null);
    } catch (error) {
      console.error("Failed to delete product:", error);
      setErrorMessage("Unable to delete product. Please try again later.");
      setShowErrorToast(true);
    }
  };

  const confirmDeleteProduct = (productId: string): void => {
    setDeletingProductId(productId);
    setShowDialog(true);
  };

  const handleConfirm = (): void => {
    if (deletingProductId) {
      handleDeleteProduct();
    }
    setShowDialog(false);
  };

  const handleCancel = (): void => {
    setDeletingProductId(null);
    setShowDialog(false);
  };

  const handleEditProduct = (productId: string, product: Product): void => {
    navigate(`../add-product?id=${productId}`, { state: product });
  };

  // Render appropriate view based on screen size
  return (
    <>
      {isMobileView ? (
        <AdminInventoryMobile
          data={products}
          isLoading={isLoading}
          error={error}
          refetch={refetch}
          onDeleteProduct={confirmDeleteProduct}
          onEditProduct={handleEditProduct}
        />
      ) : (
        <AdminInventoryPage />
      )}

      {/* Confirmation Dialog for deletion - shared between views */}
      <ConfirmationDialog
        message="Are you sure you want to delete this product? This action cannot be undone."
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isOpen={showDialog}
        title="Delete Product"
      />
      
      {/* Error Toast - shared between views */}
      {showErrorToast && (
        <div className="fixed bottom-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
          <div className="flex">
            <div className="py-1">
              <svg className="h-6 w-6 text-red-500 mr-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="font-bold">Error</p>
              <p className="text-sm">{errorMessage}</p>
            </div>
            <button 
              onClick={() => setShowErrorToast(false)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ResponsiveAdminInventory;